import React, { useState, useRef, useEffect, useMemo } from 'react';

// Searchable "Offering type" combobox — required field for a New Session.
// No searchable-popover pattern existed anywhere in this codebase before this
// component; the click-outside/Escape handling here is the first instance of
// that pattern and is intentionally self-contained rather than extracted into
// a shared primitive (see ClientAddPicker.js, which duplicates this shape
// rather than sharing it, per this codebase's preference for a little
// duplication over a premature abstraction).
export default function OfferingTypePicker({ offerings, selectedOfferingId, onSelect, onCreateNew }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOffering = offerings.find(o => o.id === selectedOfferingId);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return offerings
      .filter(o => o.is_active || o.id === selectedOfferingId)
      .filter(o => !q || o.name.toLowerCase().includes(q));
  }, [offerings, selectedOfferingId, search]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setSearch('');
      searchInputRef.current?.focus();
    }
  }, [open]);

  const renderBadge = (o) => {
    if (!o.format) return null;
    const isGroup = o.format === 'group';
    return (
      <span style={{ ...styles.badge, background: isGroup ? 'var(--border-light)' : 'var(--gold-pale)', color: isGroup ? 'var(--text-mid)' : 'var(--gold)' }}>
        {isGroup ? 'Group' : '1:1'}
      </span>
    );
  };

  return (
    <div style={styles.wrapper} ref={wrapperRef}>
      <label style={styles.label}>
        Offering type {!selectedOfferingId && <span style={styles.required}>· required</span>}
      </label>

      {selectedOfferingId && !open ? (
        <div style={styles.confirmedRow} onClick={() => setOpen(true)}>
          <span style={styles.confirmedIcon}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
              <path d="M8 1.5l6 3-6 3-6-3 6-3z" />
              <path d="M2 8l6 3 6-3" />
              <path d="M2 11l6 3 6-3" />
            </svg>
          </span>
          <div style={{ flex: 1 }}>
            <div style={styles.confirmedValueRow}>
              <span style={styles.confirmedName}>{selectedOffering?.name || '—'}</span>
              {selectedOffering && renderBadge(selectedOffering)}
            </div>
          </div>
          <span style={styles.chevron}>▾</span>
        </div>
      ) : (
        <div style={styles.trigger} onClick={() => setOpen(true)}>
          <span style={styles.searchIcon}>🔍</span>
          <span style={styles.triggerPlaceholder}>Search offering types</span>
        </div>
      )}

      {open && (
        <div style={styles.popover}>
          <input
            ref={searchInputRef}
            style={styles.searchInput}
            placeholder="Search offering types"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={styles.list}>
            {filtered.map(o => (
              <div
                key={o.id}
                style={styles.row}
                onClick={() => { onSelect(o.id); setOpen(false); }}
              >
                <span style={styles.rowName}>{o.name}</span>
                {renderBadge(o)}
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={styles.emptyState}>No offerings match "{search}".</div>
            )}
          </div>
          <div style={styles.footer} onClick={() => { onCreateNew(); setOpen(false); }}>
            <span style={styles.footerAdd}>+ New offering type</span>
            <span style={styles.footerCount}>{offerings.filter(o => o.is_active).length} total</span>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper: { position: 'relative', marginBottom: '20px' },
  label: { display: 'block', fontSize: '11.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-soft)', marginBottom: '8px' },
  required: { color: 'var(--gold)', textTransform: 'none', letterSpacing: 'normal', fontWeight: '500' },
  trigger: { display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', cursor: 'pointer' },
  searchIcon: { fontSize: '13px', opacity: 0.6 },
  triggerPlaceholder: { fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-faint)' },
  confirmedRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', borderRadius: '10px', border: '1px solid var(--gold-pale)', background: 'var(--gold-pale)', cursor: 'pointer' },
  confirmedIcon: { color: 'var(--gold)', display: 'flex', alignItems: 'center' },
  confirmedValueRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  confirmedName: { fontFamily: "'DM Sans', sans-serif", fontSize: '14.5px', fontWeight: '500', color: 'var(--text-dark)' },
  chevron: { color: 'var(--gold)', fontSize: '12px' },
  badge: { fontSize: '11px', padding: '2px 8px', borderRadius: '20px', fontWeight: '500', whiteSpace: 'nowrap' },
  popover: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50, background: 'white', border: '1px solid var(--border)', borderRadius: '10px', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', maxHeight: '340px', overflow: 'hidden' },
  searchInput: { width: '100%', padding: '12px 14px', border: 'none', borderBottom: '1px solid var(--border-light)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box', flexShrink: 0 },
  list: { overflowY: 'auto', flex: 1 },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-light)' },
  rowName: { fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)' },
  emptyState: { padding: '16px 14px', fontSize: '13px', color: 'var(--text-soft)' },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderTop: '1px solid var(--border-light)', background: 'var(--gold-pale)', cursor: 'pointer', flexShrink: 0 },
  footerAdd: { fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', fontWeight: '600', color: 'var(--gold)' },
  footerCount: { fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: 'var(--text-soft)' },
};
